import { Injectable, inject } from "@angular/core";
import { environment } from "../../../environments/environment";
import { forkJoin, from, Observable, of } from "rxjs";
import { catchError, map, mergeMap, retry, tap, timeout } from "rxjs/operators";
import { LoggerService } from "./logger.service";
import { PcsApiService } from "./pcs-api.service";
import { Currency } from "../models/currency";
import Swal from "sweetalert2";
import {
  CloudProvider,
  FilterableL2,
  FiltersType,
  GroupByPayload,
  Level,
  QueryFilterSimple,
  QueryMetadata,
} from "../models/vcloud-api";
import { Filter } from "../models/filtering";
import { ROWS_PER_PAGE } from "../constant/vcloud-api";
import { CSP } from "../models/csp-api";
import { MAX_EXPORT_DURATION, MAX_EXPORT_PARALLELISM } from "../constant/export";
import { CLOUD_PROVIDERS_ALL } from "../constant/llm";
import { ApplyShareLinkFiltersParams, CustomReport, ReportShare } from "../models/report-sharing";
import { FormattingService } from "./formatting.service";
import { VcloudHttpClientService } from "./vcloud-http-client.service";
import { VcloudCostTrackerService } from "./vcloud-cost-tracker.service";
import { VcloudDebugCaptureService } from "./vcloud-debug-capture.service";
import { VcloudFiltersApiService } from "./vcloud-filters-api.service";

const SECOND = 1000;

@Injectable({
  providedIn: "root",
})
export class VCloudSmartApiService {
  private formatting = inject(FormattingService);
  private log = inject(LoggerService);
  private pcsApi = inject(PcsApiService);
  private httpClient = inject(VcloudHttpClientService);
  private costTracker = inject(VcloudCostTrackerService);
  private debugCapture = inject(VcloudDebugCaptureService);
  private filtersApi = inject(VcloudFiltersApiService);
  public getCustomReports(): Observable<CustomReport[]> {
    return this.pcsApi.get<{ Resources: CustomReport[] }>("tenancies-v1/share-codes").pipe(
      map((r) => {
        return (r?.Resources ?? []).filter((r: CustomReport) => {
          const t: string = (r as ReportShare)?.Target || "";
          return !t.includes("/recently-visited/");
        });
      }),
      map((reports: CustomReport[]) => {
        const deduplicatedReports: CustomReport[] = [];
        for (const r of reports) {
          if (
            !deduplicatedReports.some(({ DisplayName, Filters, ...rest }) => {
              return (
                (rest as ReportShare).Target === (r as ReportShare)?.Target &&
                Filters === r?.Filters &&
                DisplayName === r?.DisplayName
              );
            })
          ) {
            deduplicatedReports.push(r);
          }
        }
        return deduplicatedReports;
      }),
    );
  }

  public processAsyncDownloadRequestBody(body: GroupByPayload): GroupByPayload {
    const clone: GroupByPayload = JSON.parse(JSON.stringify(body));
    if (clone?.Limit !== undefined) {
      delete clone.Limit;
    }
    if (clone?.Offset !== undefined) {
      delete clone.Limit;
    }
    if (clone?.OrderBy !== undefined) {
      delete clone.OrderBy;
    }
    if (clone?.SortOrder !== undefined) {
      delete clone.SortOrder;
    }
    return clone;
  }

  public preProcessRequestFilters(allFilters: (QueryFilterSimple | FilterableL2)[]): QueryFilterSimple[] {
    const simplifiedFilters = allFilters
      .filter((f) => (f as FilterableL2).Filters === undefined)
      .map((f) => this.filtersApi.simplifyFilter(f as QueryFilterSimple));

    const deduplicatedSimplifedFilters = this.filtersApi.deduplicateFilters(simplifiedFilters);

    const orderedSimpleFilters = deduplicatedSimplifedFilters
      .filter(
        (f) =>
          (f as QueryFilterSimple).Type === FiltersType.date || (f as QueryFilterSimple).Type === FiltersType.timestamp,
      )
      .concat(
        this.filtersApi.orderFilters(
          deduplicatedSimplifedFilters.filter(
            (f) =>
              (f as QueryFilterSimple).Type !== FiltersType.date &&
              (f as QueryFilterSimple).Type !== FiltersType.timestamp,
          ) as QueryFilterSimple[],
        ),
      );

    const complexFilters = allFilters.filter((f) => (f as FilterableL2).Filters !== undefined);

    return orderedSimpleFilters.concat(complexFilters) as QueryFilterSimple[];
  }
  public getLevelsForMetadata(csp: CSP): Level[] {
    const o: { [csp: string]: { id: string; label?: string }[] } = {
      aws: [
        {
          id: "account_id",
        },
        {
          id: "account_name",
        },
        {
          id: "status",
          label: "Account Status",
        },
        {
          id: "local_market",
          label: "Master Account",
        },
        {
          id: "managed_by",
        },
        {
          id: "business_service",
        },
        {
          id: "environment",
        },
        {
          id: "vams_id",
        },
        {
          id: "business_email",
        },
        {
          id: "confidentiality",
        },
        {
          id: "technical_email",
        },
        {
          id: "sec_assessment",
          label: "Security Assessment ID",
        },
      ],
      gcp: [
        {
          id: "project_id",
        },
        {
          id: "po_number",
        },
        {
          id: "project_name",
        },
        {
          id: "project_number",
        },
        {
          id: "business_service",
        },
        {
          id: "cost_centre",
        },
        {
          id: "wbs",
        },
        {
          id: "programme",
        },
        {
          id: "sec_assessment",
          label: "Security Assessment ID",
        },
        {
          id: "project_owner",
          label: "Business Owner",
        },
        {
          id: "data_governance_compliance_manager",
          label: "Data Governance & Compliance Manager",
        },
        {
          id: "managed_by",
        },
        {
          id: "confidentiality",
        },
        {
          id: "environment",
        },
        {
          id: "local_market",
        },
        {
          id: "budget_owner",
          label: "Cost Centre Owner",
        },
        {
          id: "status",
          label: "Project Status",
        },
        {
          id: "bu",
          label: "Business Unit",
        },
      ],
      oci: [
        {
          id: "compartment_name",
        },
        {
          id: "compartment_id",
        },
        {
          id: "compartment_status",
        },
        {
          id: "parent_id",
        },
        {
          id: "parent_type",
        },
        {
          id: "parent_type",
        },
        {
          id: "budget_owner",
        },
        {
          id: "compartment_owner",
        },
        {
          id: "programme",
        },
        {
          id: "ownership_type",
        },
        {
          id: "technical_owner",
        },
        {
          id: "confidentiality",
        },
        {
          id: "cost_centre",
        },
        {
          id: "service_class",
        },
        {
          id: "portfolio",
        },
        {
          id: "managed_by",
        },
        {
          id: "local_market",
        },
      ],
      drcc: [
        {
          id: "compartment_name",
        },
        {
          id: "compartment_id",
        },
        {
          id: "compartment_description",
        },
        {
          id: "tenant_name",
        },
        {
          id: "tenant_id",
        },
        {
          id: "budget_owner",
        },
        {
          id: "created_by",
        },
        {
          id: "home_region",
        },
        {
          id: "compartment_status",
        },
        {
          id: "parent_name",
        },
        {
          id: "parent_id",
        },
        {
          id: "business_service",
        },
        {
          id: "compartment_owner",
        },
        {
          id: "confidentiality",
        },
        {
          id: "compartment_type",
        },
        {
          id: "environment",
        },
        {
          id: "local_market",
        },
        {
          id: "managed_by",
        },
        {
          id: "portfolio",
        },
        {
          id: "programme",
        },
        {
          id: "service_class",
        },
        {
          id: "technical_owner",
        },
        {
          id: "compartment_category",
        },
        {
          id: "request_id",
        },
      ],
      azure: [
        {
          id: "programme",
        },
        {
          id: "cost_centre",
        },
        {
          id: "budget_owner",
        },
        {
          id: "status",
          label: "Subscription Status",
        },
        {
          id: "group",
        },
        {
          id: "enrollment_account",
        },
        {
          id: "subscription_owner",
        },
        {
          id: "subscription_name",
        },
        {
          id: "subscription_id",
        },
        {
          id: "managed_by",
        },
        {
          id: "technical_owner",
        },
        {
          id: "vams_id",
        },
        {
          id: "environment",
        },
      ],
    };
    return o[csp].map((v) => ({ ...v, table: "Table_2" }));
  }

  // only to be used with CostDaily data store
  public get azureFilters(): Filter[] {
    return this.filtersApi.getAzureFilters((date, months, dayNumber) => this.addMonths(date, months, dayNumber));
  }

  public decodeGCPEmail(email: string): string {
    return this.formatting.decodeGCPEmail(email);
  }

  /**
   * This helper function will convert shareId={"service": ["EC2, "ECS", ...]} into filters
   *
   * It's an alternative to using the API-based shareId code for applying filters.
   *
   * For example, you can use it to generate links to other reports in VCloudSmart where some filters are pre-applied.
   */
  public extractFiltersFromShareId(shareId: string): {
    Filters: { all: Array<{ any: Array<{ eq: [string, string] }> }> };
  } {
    return this.filtersApi.extractFiltersFromShareId(shareId);
  }

  private async groupByGetAllHelper<T extends string | number>(
    { Limit, Offset, ...body }: GroupByPayload,
    pageSize: number,
    exportDuration: number = MAX_EXPORT_DURATION,
    parallelism: number = MAX_EXPORT_PARALLELISM,
  ): Promise<{ Results: { Group: string[]; Value: T[] }[] }> {
    let offset = 0;
    let allRows = [];
    const startTime = new Date().getTime();
    while (true) {
      this.log.info("processing next batch for export...");
      const tasks: Array<Promise<{ Results: { Group: string[]; Value: T[] }[]; Metadata?: QueryMetadata }>> = [];
      for (let i = 0; i < parallelism; i++) {
        this.log.info({ task: i, offset, pageSize });
        tasks.push(this.groupBy<T>({ ...body, Limit: pageSize, Offset: offset }).toPromise());
        offset += pageSize;
      }
      let lastResponse: { Results: { Group: string[]; Value: T[] }[]; Metadata?: QueryMetadata };
      for (const t of tasks) {
        let response: { Results: { Group: string[]; Value: T[] }[]; Metadata?: QueryMetadata };
        try {
          response = await t;
        } catch (e) {
          this.log.error(e);
          await Swal.fire({
            title: "Error",
            html: `An error has been encountered while exporting data. Retrieved only the first ${allRows.length} rows.`,
            icon: "error",
          });
          return { ...lastResponse, Results: allRows };
        }
        allRows = allRows.concat(response?.Results ?? []);
        lastResponse = response;
        this.log.info({ lastResponse, allRows, response });
      }
      const now = new Date().getTime();
      const timeExceeded = now - startTime >= exportDuration;
      const noMoreData = (lastResponse?.Results?.length ?? 0) < pageSize;
      this.log.info({ now, startTime, timeExceeded, noMoreData });
      if (noMoreData || timeExceeded) {
        if (timeExceeded) {
          await Swal.fire({
            title: "Warning",
            html: `You have reached the limit of ${Math.ceil(
              exportDuration / SECOND,
            )} seconds. Retrieved only the first ${allRows.length} rows.`,
            icon: "warning",
          });
        }
        return { ...lastResponse, Results: allRows };
      }
    }
  }
  public getFilters(
    mergeFilters: Filter[],
    payload: Partial<GroupByPayload> & { CloudProvider: CloudProvider; DataStore: string },
    shareId: string,
    tryRecallPreviousFilters: boolean = true,
    isSharedReport: boolean = false,
  ): Observable<QueryFilterSimple[]> {
    // page components need to wait for this request
    // and apply the filters to the initial requests used to populate the page
    return (
      shareId
        ? shareId.startsWith("{")
          ? of(this.extractFiltersFromShareId(shareId))
          : this.pcsApi
              .get<{
                DisplayName?: string;
                Filters: { all: Array<{ any: Array<{ eq: [string, string] }> }> };
              }>(`tenancies-v1/share-codes/${shareId}`)
              .pipe(
                catchError((e) => {
                  this.log.error(e);
                  return of({ Filters: { all: [] } });
                }),
              )
        : of({ Filters: { all: [] } })
    ).pipe(
      mergeMap((share) => {
        try {
          if (typeof share?.Filters === "string") {
            share.Filters = JSON.parse(share?.Filters);
          }
        } catch (e) {
          this.log.error(e);
          share.Filters = { all: [] };
        }
        if ((share?.Filters?.all?.length ?? 0) === 0) {
          this.injectFilterValues(mergeFilters, payload, isSharedReport).subscribe((result) => {
            this.log.info({ msg: "injected filter values", result, share, mergeFilters, payload, shareId });
          });
          return of(this.filtersApi.convertToBqFilters(mergeFilters));
        } else {
          this.filtersApi.applyShareLinkFilters(share, mergeFilters);
          this.log.info({ msg: "applied share link to filters", share, mergeFilters, payload, shareId });
          return this.handleFiltersChange(mergeFilters, payload, true).pipe(map(({ vCloudFilters }) => vCloudFilters));
        }
      }),
      map((filters: QueryFilterSimple[]) => {
        const segments: string[] = location.pathname
          .split("/")
          .filter(Boolean)
          .map((s: string) => s.toLowerCase());
        const hashSegments: string[] = ((location?.hash || "").split("#").filter(Boolean)?.[0] || "")
          .split("/")
          .filter(Boolean);
        const csp: CloudProvider = CLOUD_PROVIDERS_ALL.find((csp: CloudProvider) => {
          return segments.includes(csp) || hashSegments.includes(csp);
        });
        let recalledSome = false;
        if (csp && !shareId && tryRecallPreviousFilters) {
          const previousFilters: QueryFilterSimple[] = this.filtersApi.tryRecallPreviousFilters(csp);
          if (previousFilters.length > 0) {
            const awsSet1 = new Set(["line_item_usage_account_id", "account_id"]);
            const awsSet2 = new Set(["awsAccountId", "account_id"]);
            const awsSet3 = new Set(["AccountId", "account_id"]);
            const ociSet1 = new Set(["product_compartmentId", "compartment_id"]);
            const azureSet1 = new Set(["cost_subscriptionId", "subscription_id"]);
            const azureSet2 = new Set(["subId", "subscription_id"]);
            const azureSet3 = new Set(["prop_subId", "subscription_id"]);
            const azureSet4 = new Set(["subscriptionId", "subscription_id"]);
            for (const f of mergeFilters) {
              if ((f.selected?.size ?? 0) !== 0) {
                continue;
              }
              const fDetails = this.filtersApi.getTableForFilter(f);
              for (const pf of previousFilters) {
                const pfDetails = this.filtersApi.getTableForQueryFilter(pf);
                const idSet: Set<string> = new Set([fDetails.id, pfDetails.id]);
                if (
                  fDetails.id !== pfDetails.id &&
                  !this.filtersApi.setEquals(idSet, awsSet1) &&
                  !this.filtersApi.setEquals(idSet, awsSet2) &&
                  !this.filtersApi.setEquals(idSet, awsSet3) &&
                  !this.filtersApi.setEquals(idSet, ociSet1) &&
                  !this.filtersApi.setEquals(idSet, azureSet1) &&
                  !this.filtersApi.setEquals(idSet, azureSet2) &&
                  !this.filtersApi.setEquals(idSet, azureSet3) &&
                  !this.filtersApi.setEquals(idSet, azureSet4)
                ) {
                  continue;
                }
                for (const pfVal of Array.isArray(pf.Value) ? pf.Value : [pf.Value]) {
                  this.log.info({
                    msg: "recalled filter based on previous usage",
                    filter: f,
                    previousFilters: pf,
                    value: pfVal,
                  });
                  if (!f.values.includes(pfVal)) {
                    f.values = [pfVal, ...(f.values || [])].sort();
                  }
                  f.selected.add(pfVal);
                  recalledSome = true;
                }
              }
            }
          }
        }
        if (recalledSome) {
          this.filtersApi.openSnackBar("Recalled previously used filters");
          return this.filtersApi.convertToBqFilters(mergeFilters, true);
        }
        return filters;
      }),
    );
  }

  public handleFiltersChange(
    filters: Filter[],
    { Filters, ...requestBody }: GroupByPayload,
    isRecallingFilters: boolean = false,
  ): Observable<{
    vCloudFilters: QueryFilterSimple[];
    filters: Filter[];
  }> {
    const isSharedReport: boolean = !!Object.fromEntries(
      location.search
        .replace("?", "")
        .split("&")
        .map((pair) => pair.split("=")),
    )?.shareId;

    if (
      !isRecallingFilters &&
      (location?.pathname || "").startsWith("/vcloudsmart/v2/") &&
      (location?.search || "").includes("shareId=")
    ) {
      isRecallingFilters = true;
    }
    for (const f of filters) {
      if (!f.datePickerRange && f?.selected?.size === 0) {
        f.values = [];
      }
    }
    return this.getFilters(
      filters,
      {
        ...requestBody,
        Filters: ((Filters ?? []) as QueryFilterSimple[]).concat(
          this.filtersApi.convertToBqFilters(filters, isRecallingFilters, isSharedReport),
        ),
      },
      null,
      isSharedReport,
    ).pipe(
      map(() => {
        return {
          filters,
          vCloudFilters: this.filtersApi.convertToBqFilters(filters, isRecallingFilters, isSharedReport),
        };
      }),
    );
  }

  private injectFilterValues(
    mergeFilters: Filter[],
    {
      OrderBy,
      Projection,
      Limit,
      SortOrder,
      Distinct,
      ..._payload
    }: Partial<GroupByPayload> & { CloudProvider: CloudProvider; DataStore: string },
    isSharedReport: boolean = false,
  ): Observable<Array<any>> {
    return forkJoin(
      mergeFilters
        .filter(({ datePickerRange, values }) => !datePickerRange && (values ?? []).length === 0)
        .map((f) => {
          const payload: GroupByPayload = {
            ...JSON.parse(JSON.stringify(_payload)),
            // inject date filters (if any)
            Filters: JSON.parse(JSON.stringify(_payload?.Filters ?? [])).concat(
              this.filtersApi.convertToBqFilters(mergeFilters, false, isSharedReport),
            ),
            Limit: f.getAll ? undefined : ROWS_PER_PAGE,
            Projection: [
              {
                Attribute: f.table ? `${f.table}.${f.id}` : f.id,
              },
            ],
            Distinct: true,
          };

          // inject date filters (if any)
          payload.Filters = ((payload.Filters as QueryFilterSimple[]) ?? []).concat(
            this.filtersApi.convertToBqFilters(mergeFilters, false, isSharedReport),
          );

          // optimisations
          if (
            payload.SelfJoin === undefined &&
            (payload.SelfJoins?.length ?? 0) === 0 &&
            (!Array.isArray(payload.Filters) || payload.Filters.length === 0) &&
            (!Array.isArray(payload.GroupBy) || payload.GroupBy.length === 0) &&
            (!Array.isArray(payload.GroupByV2) || payload.GroupByV2.length === 0)
          ) {
            if (f.table === "Table_1") {
              // no need to join to another table
              delete payload.Join;
            } else if (f.table === "Table_2") {
              // if joining to another table but only getting the column from the other table,
              // then there is no need to query the first table at all
              payload.DataStore = payload.Join.DataStore;
              payload.Projection = payload.Projection.map(({ Attribute, ...rest }) => ({
                Attribute: Attribute.replace(/^Table_2/, "Table_1"),
                ...rest,
              }));
              delete payload.Join;
            }
          }

          if (payload?.Join?.Type !== "LEFT" && payload?.Join?.DataStore === "CloudResourcesMetadata") {
            payload.Join.Type = "LEFT";
          }

          const ops = [
            map((response: { Results: { Group: string[]; Value: string[] }[]; Metadata: QueryMetadata }) => {
              const newValues = (response?.Results ?? [])
                .filter(({ Value }) => Array.isArray(Value))
                .map(({ Value }) => Value[0]);
              f.values = [...new Set((f.values ?? []).concat(newValues))].sort();
              this.log.info({ msg: "injected filter value", filter: f });
            }),
            catchError((e) => {
              this.log.error(e);
              return of(undefined);
            }),
          ];

          /* if (
              (payload?.DataStore === "CloudResourcesMetadata" ||
                payload?.Join?.DataStore === "CloudResourcesMetadata") &&
              payload?.Projection?.length === 1 &&
              !(payload?.Filters ?? []).some(
                (f) => ((f as QueryFilterSimple)?.Attribute ?? "").split(".").slice(-1)[0] === "status"
              ) &&
              !(payload?.Projection ?? []).some((f) => (f?.Attribute ?? "").split(".").slice(-1)[0] === "status")
            ) {
              if (payload.CloudProvider === "aws") {
                payload.Filters = (payload?.Filters ?? []).concat([
                  {
                    Type: "STRING",
                    Attribute: "status",
                    Operator: "IN",
                    Table: payload?.DataStore === "CloudResourcesMetadata" ? "Table_1" : "Table_2",
                    Value: ["ACTIVE", "deployed"],
                  } as QueryFilterSimple,
                ]);
              } else if (payload.CloudProvider === "gcp") {
                payload.Filters = (payload?.Filters ?? []).concat([
                  {
                    Type: "STRING",
                    Attribute: "status",
                    Operator: "IN",
                    Table: payload?.DataStore === "CloudResourcesMetadata" ? "Table_1" : "Table_2",
                    Value: ["ACTIVE", "deployed"],
                  } as QueryFilterSimple,
                ]);
              } else if (payload.CloudProvider === "azure") {
                payload.Filters = (payload?.Filters ?? []).concat([
                  {
                    Type: "STRING",
                    Attribute: "status",
                    Operator: "!=",
                    Table: payload?.DataStore === "CloudResourcesMetadata" ? "Table_1" : "Table_2",
                    Value: "Deleted",
                  } as QueryFilterSimple,
                ]);
              }
            } */

          if (f.getAll) {
            // @ts-ignore
            return this.groupByGetAll<string>(payload).pipe(...ops);
          } else {
            // @ts-ignore
            return this.groupBy<string>(payload).pipe(...ops);
          }
        }),
    ).pipe(
      catchError((e) => {
        this.log.error(e);
        return of([]);
      }),
    );
  }
  /**
   * WARNING:
   *
   * this should only be used in exceptional circumstances,
   * it will paginate through the entire content of the table and return ALL rows.
   *
   * Good page value sizes are between 2000 and 5000.
   */
  public groupByGetAll<T extends string | number>(
    body: GroupByPayload,
    pageSize = ROWS_PER_PAGE,
    exportDuration = MAX_EXPORT_DURATION,
    parallelism = MAX_EXPORT_PARALLELISM,
  ): Observable<{ Results: { Group: string[]; Value: T[] }[] }> {
    return from(this.groupByGetAllHelper<T>(body, pageSize, exportDuration, parallelism));
  }
  public groupByAsync(body: GroupByPayload, currency?: Currency): Observable<void> {
    const requestBody = JSON.parse(this.JSONStringifyStable(body));

    if (requestBody.Offset) {
      delete requestBody.Offset;
    }

    if (requestBody.Limit) {
      delete requestBody.Limit;
    }

    requestBody.Filters = this.filtersApi.preProcessRequestFilters(requestBody?.Filters ?? []);

    const url = location.href.replace(location.origin, "");

    const segments: string[] = url
      .split("/")
      .filter(Boolean)
      .filter((s) => s !== "v2" && s !== "vcloudsmart");

    return this.httpClient.post("queries/group-by", {
      ...requestBody,
      Async: true,
      Metadata: Object.fromEntries(
        Object.entries({
          Url: url,
          CloudProvider: location.pathname
            .split("/")
            .map((s) => s.trim().toLowerCase())
            .find((segment) => CLOUD_PROVIDERS_ALL.concat("evo" as CloudProvider).includes(segment as CloudProvider)),
          CurrencyExchangeRate: currency?.exchangeRate,
          Section: segments[1],
          Page: segments[2],
        }).filter((e) => !!e[1]),
      ),
    });
  }

  private preProcessResponseBody<T>(Results: { Group: string[]; Value: T[] }[]) {
    const resultsSafe = Results ?? [];
    for (const r of resultsSafe) {
      for (let i = 0; i < (r?.Value?.length ?? 0); i++) {
        // @ts-ignore
        if (r?.Value[i] === -0) {
          // @ts-ignore
          r.Value[i] = 0;
        }
      }
      for (let i = 0; i < (r?.Group?.length ?? 0); i++) {
        // @ts-ignore
        if (r?.Group[i] === -0) {
          // @ts-ignore
          r.Group[i] = 0;
        }
      }
    }
  }

  // private tryCacheResponseBody<T>(
  //   cacheKey: string,
  //   requestBody: GroupByPayload,
  //   Results: { Group: string[]; Value: T[] }[]
  // ) {
  //   // avoid messing with pagination
  //   if ((requestBody?.Offset ?? 0) !== 0) {
  //     return;
  //   }
  //
  //   const resultsSafe = Results ?? [];
  //   const cols = requestBody?.Projection?.length ?? 1;
  //   const rows = resultsSafe?.length ?? 0;
  //
  //   if (rows * cols >= ROWS_PER_PAGE || rows * cols === 0) {
  //     return;
  //   }
  //
  //   const storageStats = this.pcsApi.howMuchStorageLeft(sessionStorage);
  //   const resultsStringified = JSON.stringify(resultsSafe);
  //   this.log.info({ storageStats, dataSize: resultsStringified.length * 2 });
  //
  //   if ((resultsStringified.length * 2) / storageStats.limit >= 0.01) {
  //     return;
  //   }
  //
  //   if (resultsStringified.length * 2 >= storageStats.bytesLeft || storageStats.percent >= 0.85) {
  //     this.log.warning("revoking response cache");
  //     for (const key of Object.keys(sessionStorage).filter((k) => k.startsWith("response-"))) {
  //       sessionStorage.removeItem(key);
  //     }
  //   }
  //
  //   this.log.info({
  //     msg: "caching",
  //     nCells: rows * cols,
  //     cacheKey,
  //     data: resultsSafe,
  //     requestBody,
  //     dataBytes: resultsStringified.length * 2,
  //     dataChars: resultsStringified.length,
  //   });
  //   sessionStorage.setItem(cacheKey, resultsStringified);
  // }

  public groupBy<T extends number | string>(
    body: GroupByPayload,
  ): Observable<{ Results: { Group: string[]; Value: T[] }[]; Metadata?: QueryMetadata }> {
    let o: Observable<{ Results: { Group: string[]; Value: T[] }[]; Metadata?: QueryMetadata }>;

    const requestBody: GroupByPayload = JSON.parse(this.JSONStringifyStable(body));

    // to facilitate caching on the front-end and back-end, we standardise the request body format below
    this.filtersApi.standardiseRequestBody(requestBody);
    this.filtersApi.preProcessFilters(requestBody);

    o = this.httpClient.post("queries/group-by", requestBody);

    return o.pipe(
      timeout(30 * 1000),
      retry(2),
      tap(({ Results }) => {
        this.preProcessResponseBody<T>(Results);
        // this.tryCacheResponseBody<T>(cacheKey, requestBody, Results);
      }),
      tap(({ Metadata }) => {
        if (!environment.production) {
          setTimeout(() => {
            this.costTracker.handleQueryCost({ Metadata: { job_id: `bq-${Metadata.job_id}`, cost: Metadata.cost } });
          }, 2000);
        }
      }),
      tap((response) =>
        this.debugCapture.captureResponse(response, body, (payload) => this.JSONStringifyStable(payload)),
      ),
    );
  }
  public JSONStringifyStable(obj: GroupByPayload): string {
    const allKeys = new Set<string>();
    obj = this.filtersApi.sortFilters(obj);

    JSON.stringify(obj, (key, value) => (allKeys.add(key), value));
    return JSON.stringify(obj, Array.from(allKeys).sort());
  }

  public addDays(date: number | string | Date, days: number): Date {
    const result = new Date(date);
    if (days !== 0) {
      result.setDate(result.getDate() + days);
    }
    return result;
  }

  // DO NOT USE, use https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString
  public months: string[] = this.getMonths();

  private getMonths(): Array<string> {
    const year = new Date().getFullYear();
    const locales = navigator.language || "en-GB";
    const options = { month: "short" as "short" };
    return Array(12)
      .fill(null)
      .map((_, i) =>
        new Date(`${year}-${(i + 1).toString().padStart(2, "0")}-01`).toLocaleDateString(locales, options),
      );
  }

  public addMonths(_date: number | string | Date, months: number, dayNumber: number = 1): Date {
    // https://stackoverflow.com/questions/2706125/javascript-function-to-add-x-months-to-a-date#2706169
    let date = new Date(_date);
    let m,
      d = (date = new Date(+date)).getDate();
    date.setMonth(date.getMonth() + months, 1);
    m = date.getMonth();
    date.setDate(d);
    if (date.getMonth() !== m) {
      date.setDate(0);
    }
    date.setDate(dayNumber);
    return date;
  }
}
